import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authClient } from '../utils/amplifyClient';
import * as Sentry from '@sentry/react';
import { ArchivedDiscrepancy, Discrepancy } from '../types/schema';
import { getArchivedDiscrepancy, getDiscrepancy } from '../graphql/queries';
import { tDiscrepancyLog } from '../types';
import {
    buildIssuePageTitle,
    buildIssueStatusLabel,
    buildIssuesListPath,
    formatReferencedComponent,
    getIssueSubtitleClassName,
    issueNoticeOriginFromRecord,
    type IssueNoticeOrigin,
} from '../utils/issueUtils';
import { buildReportPath } from '../utils/reportUrlUtils';
import { resolveReportSource } from '../utils/resolveReportSource';
import type { ReportSource } from '../utils/reportUrlUtils';
import { ITEM_STATUS_CODES, ITEM_STATUS_CONTENT } from '../utils/constants';
import { hasPermission, Permission } from '../utils/permissions';
import { useUserContext } from '../contexts/UserContext';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { BackButton, Btn, Card, IconAlertTriangle, IconPencil, IconReports, PageHeader } from './ui';
import CardHeader from './ui/headers/CardHeader';
import { IssueUpdateStatusModal } from './issues/IssueUpdateStatusModal';
import { IssueResolveArchiveModal } from './issues/IssueResolveArchiveModal';
import '../css/IssuesContent.css';

type IssueRecord = Discrepancy | ArchivedDiscrepancy;

interface IssueDetailProps {
    editMode?: boolean;
}

const client = authClient;

const getStatusClass = (status: string) => {
    switch (status) {
        case ITEM_STATUS_CODES.inop:
            return 'status-inop';
        case ITEM_STATUS_CODES.warn:
            return 'status-warn';
        case ITEM_STATUS_CODES.under_review:
            return 'status-under-review';
        default:
            return 'status-ok';
    }
};

const getStatusLabel = (status: string) => {
    switch (status) {
        case ITEM_STATUS_CODES.inop:
            return ITEM_STATUS_CONTENT.inop;
        case ITEM_STATUS_CODES.warn:
            return ITEM_STATUS_CONTENT.warn;
        case ITEM_STATUS_CODES.under_review:
            return ITEM_STATUS_CONTENT.under_review;
        default:
            return ITEM_STATUS_CONTENT.resolved;
    }
};

const parseIssueLog = (log?: string | null): tDiscrepancyLog[] => {
    if (!log) {
        return [];
    }

    try {
        const parsed = JSON.parse(log);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const formatHistoryDate = (date: string) => {
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return `${parsedDate.toLocaleDateString()} ${parsedDate.toLocaleTimeString()}`;
};

export const IssueDetail = ({ editMode = false }: IssueDetailProps) => {
    const { id, issueId } = useParams();
    const resolvedIssueId = id || issueId;
    const navigate = useNavigate();
    const { companyUser, companyOwner, businessAdmin } = useUserContext();
    const { companyUnits } = useEquipmentContext();
    const { companyLocations } = useRegionAndLocationContext();
    const [issue, setIssue] = useState<IssueRecord | null>(null);
    const [isArchivedIssue, setIsArchivedIssue] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(editMode);
    const [isResolveArchiveModalOpen, setIsResolveArchiveModalOpen] = useState(false);
    const [reportSource, setReportSource] = useState<ReportSource | null>(null);
    const [noticeOrigin, setNoticeOrigin] = useState<IssueNoticeOrigin | null>(null);

    const canUpdateDiscrepancies = hasPermission(
        companyOwner,
        companyUser,
        Permission.UPDATE_DISCREPANCIES,
        businessAdmin,
    );
    const canArchiveDiscrepancies = hasPermission(
        companyOwner,
        companyUser,
        Permission.ARCHIVE_DISCREPANCIES,
        businessAdmin,
    );
    const canViewReports = hasPermission(companyOwner, companyUser, Permission.VIEW_REPORTS, businessAdmin);

    const fetchIssue = useCallback(async () => {
        if (!resolvedIssueId) {
            setNotFound(true);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setNotFound(false);

        try {
            const currentResult = await client.graphql({
                query: getDiscrepancy,
                variables: { id: resolvedIssueId },
            });

            if (currentResult.data?.getDiscrepancy) {
                setIssue(currentResult.data.getDiscrepancy);
                setIsArchivedIssue(false);
                return;
            }

            const archivedResult = await client.graphql({
                query: getArchivedDiscrepancy,
                variables: { id: resolvedIssueId },
            });

            if (archivedResult.data?.getArchivedDiscrepancy) {
                setIssue(archivedResult.data.getArchivedDiscrepancy);
                setIsArchivedIssue(true);
                return;
            }

            setIssue(null);
            setNotFound(true);
        } catch (error) {
            console.error('Error fetching issue:', error);
            Sentry.captureException(error);
            setIssue(null);
            setNotFound(true);
        } finally {
            setIsLoading(false);
        }
    }, [resolvedIssueId]);

    useEffect(() => {
        fetchIssue();
    }, [fetchIssue]);

    useEffect(() => {
        setIsEditModalOpen(editMode);
    }, [editMode]);

    useEffect(() => {
        if (!editMode || isLoading || !issue) {
            return;
        }

        if (isArchivedIssue || !canUpdateDiscrepancies) {
            navigate(`/issue/${resolvedIssueId}`, { replace: true });
        }
    }, [canUpdateDiscrepancies, editMode, isArchivedIssue, isLoading, issue, navigate, resolvedIssueId]);

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        if (editMode && resolvedIssueId) {
            navigate(`/issue/${resolvedIssueId}`, { replace: true });
        }
    };

    useEffect(() => {
        if (!issue) {
            setReportSource(null);
            setNoticeOrigin(null);
            return;
        }

        const knownOrigin = issueNoticeOriginFromRecord(issue);
        setNoticeOrigin(knownOrigin);

        if (!issue.reportId) {
            setReportSource(null);
            return;
        }

        let isActive = true;

        resolveReportSource(issue.reportId).then((source) => {
            if (!isActive) return;
            setReportSource(source);
            if (!knownOrigin && source === 'InspectionReport') setNoticeOrigin('Inspection');
            if (!knownOrigin && source === 'DailyServiceReport') setNoticeOrigin('Daily Service');
        });

        return () => {
            isActive = false;
        };
    }, [issue]);

    const handleOpenEditModal = () => {
        if (!resolvedIssueId) {
            return;
        }
        navigate(`/issues/${resolvedIssueId}/edit`);
    };

    const historyEntries = useMemo(() => parseIssueLog(issue?.log), [issue?.log]);
    const issueUnit = useMemo(
        () => companyUnits.find((unit) => unit.id === issue?.unitId) ?? null,
        [companyUnits, issue?.unitId],
    );
    const issueUnitName = issueUnit?.name || issue?.equipmentName || '';
    const issueLocationName = companyLocations.find((location) => location.id === issueUnit?.locationId)?.name || '';
    const noticedAt = issue?.createdAt ? formatHistoryDate(issue.createdAt) : '';
    const editableDiscrepancy = !isArchivedIssue && issue ? (issue as Discrepancy) : null;
    const issuesListPath = buildIssuesListPath(isArchivedIssue ? 'archived' : 'current');

    const reportPath =
        issue?.reportId && reportSource
            ? buildReportPath({
                  id: issue.reportId,
                  source: reportSource,
                  slugLabel: issue.equipmentName,
              })
            : null;

    if (isLoading) {
        return (
            <div className="page-container issue-detail">
                <p role="status" aria-live="polite">
                    Loading issue...
                </p>
            </div>
        );
    }

    if (notFound || !issue) {
        return (
            <div className="page-container issue-detail">
                <PageHeader
                    as="h1"
                    title="Issue not found"
                    actions={
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Back"
                            buttonOnClick={() => navigate(buildIssuesListPath('current'))}
                        />
                    }
                />
            </div>
        );
    }

    return (
        <div className="page-container issue-detail">
            <PageHeader
                as="h1"
                    title={issueUnitName || 'Unknown Equipment'}
                subtitle={buildIssueStatusLabel(issue.status)}
                subtitleClassName={getIssueSubtitleClassName(issue.status)}
                breadcrumbs={[
                    { label: 'Issues', href: issuesListPath },
                    { label: buildIssuePageTitle(issue.status, issueUnitName) },
                ]}
            />

            <Card variant="card-lg" className="issue-detail-card">
                <BackButton to={issuesListPath} />
                <CardHeader
                    as="h2"
                    title={formatReferencedComponent(issue)}
                    subtitle={issueUnitName || 'Unknown Equipment'}
                />
                <dl className="issue-detail-facts">
                    <div>
                        <dt>Equipment</dt>
                        <dd>{issueUnitName || 'Unknown Equipment'}</dd>
                    </div>
                    <div>
                        <dt>Location</dt>
                        <dd>{issueLocationName || 'Unassigned'}</dd>
                    </div>
                    <div>
                        <dt>Noticed</dt>
                        <dd>{noticedAt || '—'}</dd>
                    </div>
                    <div>
                        <dt>From</dt>
                        <dd>{noticeOrigin || '—'}</dd>
                    </div>
                </dl>
                <div className="issue-detail-body">
                    <div className="issue-detail-actions">
                        <div className="issue-detail-status">
                            <span className="issue-detail-label">Status</span>
                            <span className={`status-badge ${getStatusClass(issue.status)}`}>
                                {getStatusLabel(issue.status)}
                            </span>
                        </div>
                        <div className="issue-detail-actions-buttons">
                            {canUpdateDiscrepancies && editableDiscrepancy && (
                                <Btn
                                    variant="primary"
                                    buttonSize="sm"
                                    buttonShape="semi-square"
                                    buttonText="Update Status"
                                    buttonIcon={<IconPencil />}
                                    buttonIconPosition="left"
                                    buttonOnClick={handleOpenEditModal}
                                />
                            )}
                            {canViewReports && reportPath && (
                                <Btn
                                    variant="secondary-outlined"
                                    buttonSize="sm"
                                    buttonShape="semi-square"
                                    buttonText="View Report"
                                    buttonIcon={<IconReports />}
                                    buttonIconPosition="left"
                                    buttonOnClick={() => navigate(reportPath)}
                                />
                            )}
                            {canArchiveDiscrepancies && editableDiscrepancy && (
                                <Btn
                                    variant="warning-outlined"
                                    buttonSize="sm"
                                    buttonShape="semi-square"
                                    buttonText="Archive Issue"
                                    buttonIcon={<IconAlertTriangle />}
                                    buttonIconPosition="left"
                                    buttonOnClick={() => setIsResolveArchiveModalOpen(true)}
                                />
                            )}
                        </div>
                    </div>

                    <h3 className="issue-detail-history-heading">Issue History</h3>
                    <div className="issue-detail-history">
                        {historyEntries.length === 0 ? (
                            <p className="issue-detail-empty">No history entries.</p>
                        ) : (
                            historyEntries.map((entry, index) => (
                                <div className="log-entry" key={`${entry.date}-${entry.name}-${index}`}>
                                    <div className="log-notes">{entry.notes || 'No notes provided.'}</div>
                                    <div className="log-author">{entry.name || 'Unknown reporter'}</div>
                                    <div className="log-date">{formatHistoryDate(entry.date)}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </Card>

            <IssueResolveArchiveModal
                discrepancy={isResolveArchiveModalOpen ? editableDiscrepancy : null}
                onClose={() => setIsResolveArchiveModalOpen(false)}
                onArchived={async () => {
                    await fetchIssue();
                    navigate(buildIssuesListPath('archived'));
                }}
            />

            <IssueUpdateStatusModal
                discrepancy={isEditModalOpen ? editableDiscrepancy : null}
                onClose={handleCloseEditModal}
                onUpdated={fetchIssue}
            />
        </div>
    );
};
